<template>
  <v-card-text>
    <v-row>
      <!-- Asignados + selector de proyecto -->
      <v-col cols="12" md="6">
        <v-card rounded="lg" variant="outlined" class="cool-card">
          <v-card-title
            class="d-flex align-center justify-space-between flex-wrap ga-2"
          >
            <div class="d-flex align-center ga-2">
              <v-icon color="primary">mdi-link</v-icon>
              <span class="text-subtitle-1 font-weight-bold">{{
                $t("subtitle_equipos_asignados")
              }}</span>
            </div>

            <div
              class="d-flex align-center ga-2 flex-wrap"
              style="justify-content: flex-end"
            >
              <v-autocomplete
                :label="$t('project')"
                v-model="selectedProjectKey"
                :items="projects"
                item-title="label"
                item-value="id"
                variant="solo"
                density="comfortable"
                hide-details
                hide-no-data
                clearable
                style="min-width: 260px"
                :loading="loadingProjects"
                :disabled="loadingProjects || projects.length === 0"
                placeholder="Selecciona un proyecto"
                @update:modelValue="onProjectChanged"
              />
            </div>
          </v-card-title>

          <v-divider />
          <v-card-text>
            <v-alert
              v-if="!selectedProjectKey"
              type="info"
              variant="tonal"
              class="mb-3"
            >
              {{ $t("alert1") }}
            </v-alert>

            <v-progress-linear
              v-if="loadingAssigned"
              indeterminate
              class="mb-3"
            />

            <v-list v-if="selectedProjectKey && !loadingAssigned">
              <v-list-item v-for="t in assignedTeams" :key="t.id">
                <template #prepend>
                  <v-avatar color="primary" variant="tonal">
                    <v-icon>mdi-account-group</v-icon>
                  </v-avatar>
                </template>

                <v-list-item-title class="font-weight-medium">{{
                  t.teamname
                }}</v-list-item-title>

                <template #append>
                  <v-tooltip text="Quitar del proyecto" location="top">
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon
                        variant="text"
                        color="error"
                        @click="removeTeam(t)"
                      >
                        <v-icon>mdi-link-off</v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </template>
              </v-list-item>

              <v-list-item v-if="assignedTeams.length === 0">
                <v-list-item-title class="text-medium-emphasis">
                  {{ $t("msg4") }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Disponibles + Asignar (un proyecto puede ir en varios equipos) -->
      <v-col cols="12" md="6">
        <v-card rounded="lg" variant="outlined">
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center ga-2">
              <v-icon color="primary">mdi-playlist-plus</v-icon>
              <span class="text-subtitle-1 font-weight-bold">{{
                $t("msg3")
              }}</span>
            </div>

            <!-- <v-btn variant="tonal" color="primary" @click="fetchAllTeams" :loading="loadingAll">
              <v-icon start>mdi-refresh</v-icon>
              Refrescar
            </v-btn> -->
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-alert
              v-if="
                selectedProjectKey &&
                availableToAssign.length === 0 &&
                !loadingAll
              "
              type="success"
              variant="tonal"
              class="mb-3"
            >
              {{ $t("msg2") }}
            </v-alert>

            <v-progress-linear v-if="loadingAll" indeterminate class="mb-3" />

            <v-slide-group
              v-if="selectedProjectKey && !loadingAll"
              show-arrows
              class="py-1"
            >
              <v-slide-group-item v-for="t in availableToAssign" :key="t.id">
                <v-chip
                  class="ma-1"
                  :color="selectedToAssign.includes(t.id) ? 'success' : 'default'"
                  :variant="
                    selectedToAssign.includes(t.id) ? 'elevated' : 'tonal'
                  "
                  @click="toggleTeamToAssign(t.id)"
                  label
                >
                  <v-icon start>mdi-plus</v-icon>
                  {{ t.teamname }}
                </v-chip>
              </v-slide-group-item>
            </v-slide-group>

            <div class="d-flex justify-end mt-4">
              <v-btn
                color="success"
                variant="elevated"
                :disabled="
                  !selectedProjectKey || selectedToAssign.length === 0
                "
                :loading="assigning"
                @click="assignTeamsToProject"
              >
                <v-icon start>mdi-link</v-icon>
                {{ $t("asignar_proyecto") }} ({{ selectedToAssign.length }})
              </v-btn>
            </div>

            <div
              v-if="selectedToAssign.length"
              class="text-caption text-medium-emphasis mt-2"
            >
              {{ $t("tip1") }} <b>{{ selectedToAssign.length }}</b>
              {{ $t("equipos_por_proyecto") }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-card-text>
</template>

<script>
import { swal } from "@/utils/swal";
import { useSettingsStore } from "@/stores/settings";
import "@/css/Global.css";
import {
  getProyectos,
  getAllTeams,
  getTeamsByProyecto,
  assignTeamsToProject,
  removeTeamFromProject,
} from "@/api/equipos.js";
export default {
  name: "TabAsignacionProyecto",
  props: {
    apiBase: { type: String, required: true },
    toast: { type: Function, required: true },
  },
  emits: ["refresh-equipos"],

  data() {
    return {
      // Proyectos
      loadingProjects: false,
      projects: [],
      selectedProjectKey: null,

      // Asignación
      loadingAssigned: false,
      assignedTeams: [],
      selectedToAssign: [], // uno o varios equipos por asignar
      assigning: false,

      // Equipos disponibles
      loadingAll: false,
      allTeams: [],
    };
  },

  computed: {
    isDark() {
      const settings = useSettingsStore();
      return settings.theme === "dark";
    },
    availableToAssign() {
      const assignedIds = new Set(this.assignedTeams.map((t) => t.id));
      return this.allTeams.filter((t) => !assignedIds.has(t.id));
    },
  },

  async mounted() {
    await Promise.all([this.fetchProjects(), this.fetchAllTeams()]);
  },
  methods: {
    swalCfg(icon, title, text) {
      return {
        icon,
        title,
        text,
        timer: 2200,
        showConfirmButton: false,
        customClass: {
          popup: this.isDark ? "mi-popup" : "mi-popup-2",
          title: "mi-titulo",
        },
      };
    },
    async onProjectChanged(val) {
      this.assignedTeams = [];
      this.selectedToAssign = [];
      if (!val) return;
      await this.loadProjectTeams();
    },

    toggleTeamToAssign(teamId) {
      // Seleccion multiple: un proyecto puede asignarse a varios equipos.
      if (this.selectedToAssign.includes(teamId)) {
        this.selectedToAssign = this.selectedToAssign.filter(
          (id) => id !== teamId,
        );
      } else {
        this.selectedToAssign = [...this.selectedToAssign, teamId]; //operador spread
      }
    },

    async fetchProjects() {
      this.loadingProjects = true;
      try {
        const raw = await getProyectos();
        this.projects = (raw || [])
          .map((p) => ({
            id: p.idProyect ?? p.id ?? p.clave,
            label: `${p.idProyect ?? p.id ?? ""}${
              p.nameProyect ? " · " + p.nameProyect : ""
            }`.trim(),
          }))
          .filter((x) => x.id);
      } catch (e) {
        this.projects = [];
      } finally {
        this.loadingProjects = false;
      }
    },

    async fetchAllTeams() {
      this.loadingAll = true;
      try {
        const raw = await getAllTeams();
        this.allTeams = raw;
      } catch (e) {
        console.error(e);
        this.allTeams = [];
      } finally {
        this.loadingAll = false;
      }
    },

    async loadProjectTeams() {
      if (!this.selectedProjectKey) return;

      this.loadingAssigned = true;
      try {
        const raw = await getTeamsByProyecto(this.selectedProjectKey);
        this.assignedTeams = raw;
        this.selectedToAssign = [];
      } catch (e) {
        console.error(e);
        this.assignedTeams = [];
      } finally {
        this.loadingAssigned = false;
      }
    },

    async assignTeamsToProject() {
      if (!this.selectedProjectKey || this.selectedToAssign.length === 0)
        return;

      const teamName = this.selectedToAssign
        .map((id) => this.allTeams.find((t) => t.id === id)?.teamname)
        .filter(Boolean)
        .join(", ");
      const ok = await swal({
        icon: "question",
        title: this.$t("assign_team_question"),
        text: this.$t("assign_team_confirm", { name: teamName }),

        showCancelButton: true,
        reverseButtons: true,

        confirmButtonText: this.$t("yes_assign"),
        cancelButtonText: this.$t("cancel"),

        iconColor: "#2563EB",
        confirmButtonColor: "#16A34A",
        cancelButtonColor: "#6B7280",

        customClass: {
          popup: this.isDark ? "mi-popup" : "mi-popup-2",
          title: "mi-titulo",
          confirmButton: "swal-btn-success",
          cancelButton: "swal-btn-cancel",
        },
      });
      if (!ok.isConfirmed) return;
      this.assigning = true;
      try {
        await assignTeamsToProject(
          this.selectedProjectKey,
          this.selectedToAssign,
        );

       await swal({
          icon: "success",
          title: this.$t("team_assigned"),
          text: this.$t("team_assigned_ok"),

          iconColor: "#16A34A",
          confirmButtonColor: "#16A34A",

          timer: 2200,
          showConfirmButton: false,
          customClass: {
            popup: this.isDark ? "mi-popup" : "mi-popup-2",
            title: "mi-titulo",
          },
        });
        this.$emit("refresh-equipos");
        await this.loadProjectTeams();
        await this.fetchAllTeams();
      } catch (e) {
        //console.error(e);
        const err = e?.response?.data || {};
        await swal(
          this.swalCfg(
            "error",
            this.$t("assign_error"),
            err?.detail || this.$t("try_again"),
          ),
        );
      } finally {
        this.assigning = false;
      }
    },

    async removeTeam(team) {
      if (!this.selectedProjectKey) return;

      const confirm = await swal({
        icon: "warning",
        title: this.$t("remove_team_question"),
        text: this.$t("remove_team_confirm", { name: team.teamname }),
        showCancelButton: true,
        confirmButtonText: this.$t("yes_remove"),
        cancelButtonText: this.$t("cancel"),
        iconColor: "#DC2626",
        confirmButtonColor: "#DC2626",
        cancelButtonColor: "#6B7280",
        customClass: {
          popup: this.isDark ? "mi-popup" : "mi-popup-2",
          title: "mi-titulo",
          confirmButton: "swal-btn-danger",
          cancelButton: "swal-btn-cancel",
        },
      });

      if (!confirm.isConfirmed) return;

      try {
        const resp = await removeTeamFromProject(
          this.selectedProjectKey,
          team.id,
        );

        if (!resp?.status || (resp.status !== 204 && resp.status !== 200)) {
          await swal(
            this.swalCfg(
              "error",
              this.$t("remove_error"),
              this.$t("try_again"),
            ),
          );
          return;
        }

        await swal({
          icon: "success",
          title: this.$t("team_unlinked"),
          text: this.$t("team_removed_project", { name: team.teamname }),
          iconColor: "#16A34A",
          confirmButtonColor: "#16A34A",
          timer: 2000,
          showConfirmButton: false,
          customClass: {
            popup: this.isDark ? "mi-popup" : "mi-popup-2",
            title: "mi-titulo",
          },
        });
        this.$emit("refresh-equipos");
        await this.loadProjectTeams();
        await this.fetchAllTeams();
      } catch (e) {
        await swal({
          icon: "error",
          title: this.$t("network_error"),
          text: this.$t("team_remove_error"),
          confirmButtonColor: "#2563EB",
          customClass: {
            popup: this.isDark ? "mi-popup" : "mi-popup-2",
            title: "mi-titulo",
          },
        });
      }
    },
  },
};
</script>
<style>
.swal-btn-danger {
  color: #ffffff !important;
  font-weight: 600;
}

.swal-btn-cancel {
  color: #ffffff !important;
  font-weight: 500;
}

.swal-btn-success {
  color: #ffffff !important;
  font-weight: 600;
}

.swal-btn-cancel {
  color: #ffffff !important;
  font-weight: 500;
}
</style>
